import React, {
  useContext,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  RecentActors as RecentActorsIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { JobDescription } from 'API';
import { debounce } from 'debounce';
import CardBase from 'components/CardBase';
import IconCircle from 'components/IconCircle';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import CompanyService from 'services/company';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { DICTIONARY } from 'shared/constants';
import { ROUTES } from 'shared/routes';

const panelStyle = {
  marginTop: '1rem',
  '& > .MuiPaper-root': {
    marginBottom: '1rem',
  },
};

const getJobFieldsStyles = (theme: Theme) => ({
  card: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    width: '150px',
  },
  field: {
    marginTop: theme.spacing(1),
  },
  formLabel: {
    margin: `${theme.spacing(1)} 0`,
  },
  icon: {
    color: theme.palette.primary4,
  },
  margin: {
    margin: `${theme.spacing(2)} 0`,
  },
  radio: {
    alignSelf: 'center',
  },
});

const EmploymentFields: React.FC = () => {
  const theme = useTheme();
  const styles = getJobFieldsStyles(theme);
  const {
    process,
    selectedCompany,
    selectedJob,
    selectedNational,
    setProcess,
    setSelectedJob,
    isExistingJob,
    setIsExistingJob,
    titleIsTaken,
    setTitleIsTaken,
  } = useContext(CreateProcessContext);
  const [jobs, setJobs] = useState<Array<JobDescription>>([]);

  const handleIsExistingJob = (event: any) => {
    const isExisting = event.target.value === 'true';
    setIsExistingJob(isExisting);
    if (!isExisting) {
      setProcess((draft) => {
        draft.jobDescriptionID = null;
      });
      setSelectedJob(undefined);
    }
  };

  const handleExistingJobSelection = (event: any) => {
    setProcess((draft) => {
      draft.jobDescriptionID = event.target.value || null;
    });
    setSelectedJob(
      jobs.find((job) => job?.id === event.target.value) || undefined,
    );
  };

  const handleJobTitleCustomChange = (event: any) => {
    setSelectedJob({ title: event.target.value } as JobDescription);
  };

  useEffect(() => {
    let ignore = false;

    const compID = process.companyID || selectedNational?.companyID;

    const load = async () => {
      const jobsList = await CompanyService.getJobDescriptions(compID!);
      if (!ignore) {
        setJobs(jobsList);
      }
    };

    if (compID) {
      load();
    } else {
      setJobs([]);
    }

    return () => { ignore = true; };
  }, [process.companyID, selectedNational?.companyID]);

  const checkJobTitleIsTaken = (companyID: string, title: string): boolean => {
    if (jobs?.length > 0) {
      return jobs.some((job) => job.companyID === companyID && job.title === title.trim());
    }

    return false;
  };

  useEffect(() => {
    let ignore = false;

    const checkJobTitle = debounce(async () => {
      const isTaken = checkJobTitleIsTaken(
        selectedCompany!.id!, selectedJob!.title!,
      );

      if (!ignore) {
        setTitleIsTaken(isTaken);
      }
    }, 200);

    if (!isExistingJob && selectedCompany?.id && selectedJob?.title) {
      checkJobTitle();
    }

    return () => { ignore = true; };
  }, [selectedJob?.title]);

  return (
    <Box sx={panelStyle}>
      <IconRow>
        <IconRowCell type="icon">
          <IconCircle Icon={RecentActorsIcon} />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="h6">
            {DICTIONARY.JobInformation}
          </Typography>
        </IconRowCell>
      </IconRow>
      <Box sx={{ mb: 4 }}>
        <RadioGroup
          aria-labelledby="existing-job-type"
          name="existing-job-type"
          value={String(isExistingJob)}
          onChange={handleIsExistingJob}
        >
          <Grid container justifyContent="space-around">
            <Grid>
              <CardBase sx={styles.card}>
                <BusinessIcon />
                <FormControlLabel
                  sx={styles.field}
                  value="true"
                  control={
                    <Radio color="primary" sx={styles.radio} />
                  }
                  labelPlacement="top"
                  label="EXISTING JOB"
                />
              </CardBase>
            </Grid>

            <Grid>
              <CardBase sx={styles.card}>
                <PersonIcon />
                <FormControlLabel
                  sx={styles.field}
                  value="false"
                  control={
                    <Radio color="primary" sx={styles.radio} />
                  }
                  labelPlacement="top"
                  label="CREATE NEW JOB"
                />
              </CardBase>
            </Grid>
          </Grid>
        </RadioGroup>
      </Box>
      {isExistingJob !== undefined && (
        <>
          {isExistingJob && selectedCompany && (
            <IconRow sx={styles.margin} isInput>
              <IconRowCell type="icon">
                <WorkIcon sx={styles.icon} />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl
                  style={{ width: '100%' }}
                >
                  {jobs.length === 0 ? (
                    <>
                      <InputLabel required shrink>
                        Job Title
                      </InputLabel>
                      <Box sx={{ mt: 4 }}>
                        <Typography>
                          No jobs have been added to this company yet.
                          {selectedCompany && (
                            <>
                              To add a reuseable job description, go to
                              {' '}
                              <Link to={`${ROUTES.companies}/${selectedCompany.slug}`}>this company&apos;s profile</Link>
                              .
                            </>
                          )}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <InputLabel htmlFor="job-select" required>
                        Job Title
                      </InputLabel>
                      <Select
                        id="job-select"
                        onChange={handleExistingJobSelection}
                        required
                        value={process.jobDescriptionID || ''}
                      >
                        {jobs.map((job) => (
                          <MenuItem key={job!.title} value={job!.id}>
                            {job!.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>
          )}

          {isExistingJob === false && selectedCompany && (
            <>
              <IconRow sx={styles.margin} isInput>
                <IconRowCell type="icon">
                  <WorkIcon sx={styles.icon} />
                </IconRowCell>
                <IconRowCell type="text">
                  <FormControl
                    style={{ width: '100%' }}
                    required
                  >
                    <InputLabel htmlFor="job-input">
                      Job Title
                    </InputLabel>
                    <Input
                      id="job-input"
                      value={selectedJob?.title || ''}
                      onChange={handleJobTitleCustomChange}
                    />
                    {titleIsTaken && (
                      <FormHelperText error>
                        {DICTIONARY.JobTitleTaken}
                      </FormHelperText>
                    )}
                  </FormControl>
                </IconRowCell>
              </IconRow>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default EmploymentFields;
